import { useQueryClient } from "@tanstack/react-query";
import { CommitmentCancelCorrectPageProps } from "features/commitments/cancel-correct/CommitmentCancelCorrectPageProps";
import {
  getFeeOffsetsTrueUpBreakdownTableDataRetrieveQueryKey,
  getFeeTrueUpBreakdownForEventTableDataRetrieveQueryKey,
  getFundFamilySubEventListQueryKey,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { useInvestorClassListSuspense } from "gen/endpoints/investor-class/investor-class";
import {
  getSecurityOwnershipCancelCorrectListQueryKey,
  useSecurityOwnershipCancelCorrect,
  useSecurityOwnershipRetrieveSuspense,
} from "gen/endpoints/security-ownership/security-ownership";
import { SecurityOwnershipCancelCorrectCreateDataRequest } from "gen/model/securityOwnershipCancelCorrectCreateDataRequest";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
} from "shared/components/content/ContentBlock";
import Form from "shared/components/form/Form";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import {
  PageWizardFormFooter,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { nowAsIsoDate } from "shared/utils/DateUtils";
import { invalidateQueryKeys } from "shared/utils/QueryUtils";

export const CommitmentCancelCorrectClassPage: React.FC = () => {
  const { t } = useTranslation(["commitment", "common", "enums"]);

  const { fundFamilyId, commitmentId, fundFamilyEventId, cancelCorrectData } =
    useOutletContext<CommitmentCancelCorrectPageProps>();

  const { data: commitment } = useSecurityOwnershipRetrieveSuspense(
    fundFamilyId,
    commitmentId,
  );

  const { mutateAsync: cancelCorrectSecurityOwnership } =
    useSecurityOwnershipCancelCorrect();

  const { data: investorClassData } =
    useInvestorClassListSuspense(fundFamilyId);

  const { navigateNext } = usePageWizard();

  const queryClient = useQueryClient();

  const currentInvestorClassId =
    cancelCorrectData?.previous_investor_class_id ||
    commitment.investor_class_id;

  const defaultValues = {
    cancel_correct_investor_class_id:
      cancelCorrectData?.new_investor_class_id || currentInvestorClassId,
    effective_date: cancelCorrectData?.effective_date || nowAsIsoDate(),
  } as SecurityOwnershipCancelCorrectCreateDataRequest;

  const form = useForm<SecurityOwnershipCancelCorrectCreateDataRequest>({
    defaultValues,
  });

  const onSubmit = async (
    data: SecurityOwnershipCancelCorrectCreateDataRequest,
  ) => {
    await cancelCorrectSecurityOwnership({
      fundFamilyId,
      securityOwnershipId: commitmentId,
      data,
      params: {
        fund_family_event_id: fundFamilyEventId,
      },
    });
  };

  const onSuccess = async () => {
    await invalidateQueryKeys(
      queryClient,
      getSecurityOwnershipCancelCorrectListQueryKey(fundFamilyId, commitmentId),
      getFeeTrueUpBreakdownForEventTableDataRetrieveQueryKey(
        fundFamilyId,
        fundFamilyEventId,
      ),
      getFeeOffsetsTrueUpBreakdownTableDataRetrieveQueryKey(
        fundFamilyId,
        fundFamilyEventId,
      ),
      getFundFamilySubEventListQueryKey(fundFamilyId, fundFamilyEventId),
    );
    navigateNext();
  };

  return (
    <Form form={form} onSubmit={onSubmit} onSuccess={onSuccess}>
      <ContentBlockGroup>
        <ContentBlock>
          <ContentBlockBody variant="form">
            <FormSelect
              name="cancel_correct_investor_class_id"
              title={t("commitment:cancelCorrect.editClass.newClass")}
            >
              {investorClassData
                .filter(
                  (investorClass) => investorClass.id != currentInvestorClassId,
                )
                .map((investorClass) => (
                  <SelectOption key={investorClass.id} value={investorClass.id}>
                    {investorClass.name}
                  </SelectOption>
                ))}
            </FormSelect>
            <FormDateInput
              name="effective_date"
              title={t("commitment:cancelCorrect.editClass.customDate")}
            />
          </ContentBlockBody>
        </ContentBlock>
      </ContentBlockGroup>
      <PageWizardFormFooter />
    </Form>
  );
};
